import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import bkstApi from "../../api";
import { getCategoryOptions } from "../../common/util";
import RadioCardGroup from "../../common/component/RadioCardGroup";

const MAX_CATEGORIES = 5;

const CATEGORY_ACTION_OPTIONS = [
  {
    value: "add",
    title: "Add Category",
    description: "Add to selected items",
  },
  {
    value: "remove",
    title: "Remove Category",
    description: "Remove from selected items",
  },
];

export default function BulkCategoryDialog({ open, selectedIds, onClose, onProductsUpdate }) {
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [saving, setSaving] = useState(false);
  const categoryOptions = getCategoryOptions();

  useEffect(() => {
    if (open) {
      setCategory("");
      setAction("");
      setSaving(false);
    }
  }, [open]);

  const handleSave = () => {
    const trimmed = (category || "").trim();
    if (!trimmed || !action || !selectedIds?.length) return;

    setSaving(true);

    bkstApi
      .put(`/my/products/category`, { ids: selectedIds, action, category: trimmed })
      .then((res) => {
        localStorage.removeItem("products");
        onProductsUpdate(
          (res.data?.updates || []).map(({ id, category: nextCategory }) => ({
            id,
            updates: { category: nextCategory },
          }))
        );
        onClose();
      })
      .catch((error) => {
        console.error("Error updating categories:", error);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const selectedCount = selectedIds?.length || 0;
  const canSave = Boolean((category || "").trim() && action);
  const categoryDisabled = !action;
  const categoryLabel =
    action === "add" ? "Category to add" : action === "remove" ? "Category to remove" : "Category";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", pr: 6 }}>
        <Typography variant="h6">Add/Remove Category</Typography>
        <Typography variant="body2" color="textSecondary">
          {selectedCount} {selectedCount === 1 ? "item" : "items"} selected
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box py={1}>
          <RadioCardGroup
            name="categoryAction"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            options={CATEGORY_ACTION_OPTIONS}
            direction="row"
          />
        </Box>
        <Box pt={1} pb={1}>
          <Autocomplete
            disabled={categoryDisabled}
            value={category}
            onChange={(e, val) => setCategory(val || "")}
            onInputChange={(e, val, reason) => {
              if (reason === "input" || reason === "clear") {
                setCategory(val || "");
              }
            }}
            options={categoryOptions}
            freeSolo
            clearOnBlur={false}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={categoryLabel}
                placeholder="e.g. Cakes, Vegan, Thanksgiving"
                required={!categoryDisabled}
              />
            )}
          />
        </Box>
        {action === "add" && (
          <Alert severity="info">
            Items that already have this category, or that already have {MAX_CATEGORIES} categories,
            will be skipped.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button fullWidth variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
      {saving && (
        <Backdrop open={true} sx={{ zIndex: 2000, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Dialog>
  );
}
